import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { buildErrorFingerprint, isClientHttpError } from '@/services/errorFingerprint';

/**
 * Centralized logger service that wraps Application Insights
 * Provides a consistent interface for logging throughout the application
 *
 * Features:
 * - Console logging in development mode
 * - Application Insights tracking in all modes (if initialized)
 * - User context tracking
 * - Custom event tracking
 * - Error normalization and tracking
 */
export class Logger {
  private appInsights: ApplicationInsights | null = null;
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
  }

  /**
   * Initialize the logger with an Application Insights instance
   * Call this once during app startup
   */
  initialize(appInsights: ApplicationInsights | null): void {
    this.appInsights = appInsights;
    if (appInsights) {
      this.info('Logger initialized with Application Insights', {
        isDevelopment: this.isDevelopment,
      });
    } else {
      console.log('[Logger] Initialized without Application Insights (console-only mode)');
    }
  }

  /**
   * Log debug messages (only in development)
   * Use for detailed diagnostic information
   */
  debug(message: string, properties?: Record<string, unknown>): void {
    if (this.isDevelopment) {
      console.log(`[DEBUG] ${message}`, properties);
    }
    // Debug logs are not sent to Application Insights to reduce noise
  }

  /**
   * Log informational messages
   * Use for general application flow and state changes
   */
  info(message: string, properties?: Record<string, unknown>): void {
    if (this.isDevelopment) {
      console.log(`[INFO] ${message}`, properties);
    }
    this.appInsights?.trackTrace({
      message,
      severityLevel: SeverityLevel.Information,
      properties,
    });
  }

  /**
   * Log warning messages
   * Use for potentially harmful situations that don't prevent execution
   */
  warn(message: string, properties?: Record<string, unknown>): void {
    if (this.isDevelopment) {
      console.warn(`[WARN] ${message}`, properties);
    }
    this.appInsights?.trackTrace({
      message,
      severityLevel: SeverityLevel.Warning,
      properties,
    });
  }

  /**
   * Log error messages
   * Use for exceptions and error conditions
   *
   * Every error carries a stable `fingerprint` property (see buildErrorFingerprint) that the error
   * triage feeder groups on. HTTP 4xx responses are expected outcomes rather than defects, so they
   * are logged as warnings, which the feeder ignores.
   */
  error(error: Error | string, properties?: Record<string, unknown>): void {
    const errorObj = error instanceof Error ? error : new Error(error);
    const context = typeof properties?.context === 'string' ? properties.context : undefined;
    const fingerprintedProperties = { ...properties, fingerprint: buildErrorFingerprint(errorObj, context) };

    if (isClientHttpError(errorObj)) {
      this.warn(errorObj.message, fingerprintedProperties);
      return;
    }

    console.error('[ERROR]', errorObj, fingerprintedProperties);

    this.appInsights?.trackException({
      exception: errorObj,
      severityLevel: SeverityLevel.Error,
      properties: fingerprintedProperties,
    });
  }

  /**
   * Track custom events
   * Use for business-specific events (e.g., "Role.Created", "Organization.Selected")
   */
  trackEvent(name: string, properties?: Record<string, unknown>): void {
    if (this.isDevelopment) {
      console.log(`[EVENT] ${name}`, properties);
    }
    this.appInsights?.trackEvent({ name, properties });
  }

  /**
   * Track page views manually (usually handled automatically by React plugin)
   * Only use if you need to override the automatic page view tracking
   */
  trackPageView(name: string, uri?: string, properties?: Record<string, unknown>): void {
    if (this.isDevelopment) {
      console.log(`[PAGE VIEW] ${name}`, { uri, ...properties });
    }
    this.appInsights?.trackPageView({ name, uri, properties });
  }

  /**
   * Set authenticated user context
   * Call this after successful authentication
   *
   * @param userId - Unique identifier for the user (e.g., Azure AD object ID)
   * @param accountId - Optional account/tenant identifier
   */
  setUser(userId: string, accountId?: string): void {
    if (this.isDevelopment) {
      console.log(`[USER CONTEXT] User set: ${userId}, Account: ${accountId || 'N/A'}`);
    }
    this.appInsights?.setAuthenticatedUserContext(userId, accountId, true);
  }

  /**
   * Clear authenticated user context
   * Call this on logout
   */
  clearUser(): void {
    if (this.isDevelopment) {
      console.log('[USER CONTEXT] User context cleared');
    }
    this.appInsights?.clearAuthenticatedUserContext();
  }

  /**
   * Flush telemetry immediately
   * Useful before page unload or critical operations
   */
  flush(): void {
    this.appInsights?.flush();
  }
}

/**
 * Singleton logger instance
 * Import and use this throughout the application
 */
export const logger = new Logger();
